import * as React from 'react';
import { useMemo } from 'react';
import MuiAppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { APP_NAVBAR_HEIGHT_PX } from '../ThemeProvider/AvenyTheme';
import { useAppBarScrollContext } from '../../Components/AppNavbar/AppBarScrollContext';

const COOLDOWN_MS = 400;
const BOTTOM_MARGIN_PX = 10;

interface IScrollStore {
  current: number | undefined;
}

function isAtBottom(target: any): boolean {
  const scrollTop = target.scrollTop ?? 0;
  const clientHeight = target.clientHeight ?? 0;
  const scrollHeight = target.scrollHeight ?? 0;
  return scrollTop + clientHeight >= scrollHeight - BOTTOM_MARGIN_PX;
}

function createStabilizedTrigger() {
  let lastValue = false;
  let lastChangeTime = 0;

  return (
    store: IScrollStore,
    options: { disableHysteresis?: boolean; threshold?: number; target?: any }
  ) => {
    const { disableHysteresis = false, threshold = 100, target } = options;
    const previous = store.current;

    if (target) {
      store.current =
        target.pageYOffset !== undefined
          ? target.pageYOffset
          : target.scrollTop;
    }

    // Compute natural trigger value (same logic as MUI default)
    let naturalValue: boolean;
    if (!disableHysteresis && previous !== undefined) {
      if (store.current! < previous) {
        // Suppress "scroll up" when at the bottom (iOS bounce)
        naturalValue =
          target && isAtBottom(target) ? lastValue : false;
      } else {
        naturalValue = store.current! > threshold;
      }
    } else {
      naturalValue = store.current! > threshold;
    }

    // If value wants to change, enforce cooldown
    if (naturalValue !== lastValue) {
      const now = Date.now();
      if (now - lastChangeTime < COOLDOWN_MS) {
        return lastValue;
      }
      lastValue = naturalValue;
      lastChangeTime = now;
    }

    return lastValue;
  };
}

interface IProps {}

/**
 * Appbar är det centrala chromet som är genomgående genom hela appen. Det är sidhuvudet där meny, ikoner och titlar visas.
 */
export const EDAppBar: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { scrollTarget } = useAppBarScrollContext();

  // Reset trigger state when scroll target changes (view switch)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getTrigger = useMemo(() => createStabilizedTrigger(), [scrollTarget]);

  const trigger = useScrollTrigger({
    // `null` disables the hook until we have a real scroll container; `undefined` would fall back to `window`.
    target: scrollTarget ?? null,
    threshold: 100,
    getTrigger,
  } as Parameters<typeof useScrollTrigger>[0]);

  const shouldHide = scrollTarget != null && trigger;

  return (
    <Box
      sx={{
        marginTop: shouldHide ? `-${APP_NAVBAR_HEIGHT_PX}px` : '0px',
        transition: 'margin-top 225ms cubic-bezier(0.4, 0, 0.2, 1)',
        flexShrink: 0,
        '@media print': {
          display: 'none',
        },
      }}
    >
      <MuiAppBar
        color="secondary"
        sx={{
          position: 'relative',
          minHeight: APP_NAVBAR_HEIGHT_PX,
          '& img:first-of-type': {},
        }}
      >
        {props.children}
      </MuiAppBar>
    </Box>
  );
};

export default EDAppBar;
